export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const AUTH_TOKEN_KEY = "plans_auth_token";

export function getToken() {
  return localStorage.getItem(AUTH_TOKEN_KEY) || "";
}
export function setToken(t) {
  localStorage.setItem(AUTH_TOKEN_KEY, t);
}
export function clearToken() {
  localStorage.removeItem(AUTH_TOKEN_KEY);
}

// UTF-8-safe base64 encode (for custom HTML section payloads).
function b64(str) {
  return btoa(unescape(encodeURIComponent(str || "")));
}

export function mediaUrl(path) {
  if (!path) return undefined;
  if (path.startsWith("http")) return path;
  if (path.startsWith("/")) return `${BACKEND_URL}${path}`;
  return path;
}

async function request(path, opts = {}) {
  const { method = "GET", body, auth = true } = opts;
  const headers = { "Content-Type": "application/json" };
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${BACKEND_URL}/api${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const detail = (data && data.detail) || `Request failed (${res.status})`;
    throw new Error(typeof detail === "string" ? detail : "Something went wrong");
  }
  return data;
}

export const api = {
  // auth (self-hosted Google OAuth — popup auth-code flow)
  authGoogle: (code) => request("/auth/google", { method: "POST", body: { code }, auth: false }),
  logout: () => request("/auth/logout", { method: "POST" }),
  me: () => request("/auth/me"),
  session: () => request("/auth/session"),
  updateProfile: (body) => request("/auth/profile", { method: "PATCH", body }),

  // groups
  listGroups: () => request("/groups"),
  createGroup: (body) => request("/groups", { method: "POST", body }),
  listGroupRequests: () => request("/group-requests"),
  approveGroupRequest: (id) => request(`/group-requests/${id}/approve`, { method: "POST" }),
  declineGroupRequest: (id) => request(`/group-requests/${id}/decline`, { method: "POST" }),
  dedupeEvents: () => request(`/admin/events/dedupe`, { method: "POST" }),
  renameChatSender: (old_name, new_name) => request(`/admin/chat-import/rename-sender`, { method: "POST", body: { old_name, new_name } }),
  getGroup: (id) => request(`/groups/${id}`),
  updateGroup: (id, body) => request(`/groups/${id}`, { method: "PATCH", body }),
  setRole: (id, memberId, role) =>
    request(`/groups/${id}/members/${memberId}`, { method: "PATCH", body: { role } }),
  removeMember: (id, memberId) =>
    request(`/groups/${id}/members/${memberId}`, { method: "DELETE" }),
  leaveGroup: (id, new_admin_id) =>
    request(`/groups/${id}/leave`, { method: "POST", body: { new_admin_id: new_admin_id || null } }),
  deleteGroup: (id) => request(`/groups/${id}`, { method: "DELETE" }),
  invite: (id, email, app_url) =>
    request(`/groups/${id}/invite`, { method: "POST", body: { email, app_url } }),
  inviteLink: (id) => request(`/groups/${id}/invite-link`),
  invitePreview: (code) => request(`/invites/${code}`, { auth: false }),
  acceptInvite: (code) => request(`/invites/${code}/accept`, { method: "POST" }),

  // events
  createEvent: (gid, body) => request(`/groups/${gid}/events`, { method: "POST", body }),
  updateEvent: (id, body) => request(`/events/${id}`, { method: "PATCH", body }),
  deleteEvent: (id) => request(`/events/${id}`, { method: "DELETE" }),
  vote: (id) => request(`/events/${id}/vote`, { method: "POST" }),
  rsvp: (id) => request(`/events/${id}/rsvp`, { method: "POST" }),
  getEvent: (id) => request(`/events/${id}`),
  addComment: (id, text) => request(`/events/${id}/comments`, { method: "POST", body: { text } }),
  deleteComment: (id) => request(`/comments/${id}`, { method: "DELETE" }),
  blast: (id, message) => request(`/events/${id}/blast`, { method: "POST", body: { message: message || null } }),
  myAgenda: () => request("/me/agenda"),

  // places (Google Maps location autocomplete — key stays server-side)
  placesAutocomplete: (input, session_token, coords) =>
    request("/places/autocomplete", { method: "POST", body: { input, session_token, latitude: coords?.lat ?? null, longitude: coords?.lng ?? null } }),
  placeDetails: (place_id, session_token) =>
    request("/places/details", { method: "POST", body: { place_id, session_token } }),

  // photos / galleries
  addPhotos: (groupId, media_ids, event_id) =>
    request(`/groups/${groupId}/photos`, { method: "POST", body: { media_ids, event_id: event_id || null } }),
  groupPhotos: (groupId, event_id) =>
    request(`/groups/${groupId}/photos${event_id ? `?event_id=${event_id}` : ""}`),
  eventPhotos: (eventId) => request(`/events/${eventId}/photos`),
  myPhotos: () => request("/me/photos"),
  deletePhoto: (photoId) => request(`/photos/${photoId}`, { method: "DELETE" }),

  // google
  googleStart: (id, redirect_uri) =>
    request(`/groups/${id}/google/start?redirect_uri=${encodeURIComponent(redirect_uri)}`),
  googleConnect: (id) => request(`/groups/${id}/google/connect`, { method: "POST" }),
  googleCalendarAuthorize: (id, return_to) =>
    request(`/auth/google/calendar/authorize?group_id=${id}&return=${encodeURIComponent(return_to)}`),
  googleDisconnect: (id) => request(`/groups/${id}/google/disconnect`, { method: "POST" }),
  listGoogleCalendars: () => request("/google/calendars"),

  // proximity (location sharing consent layer)
  getProximityPrefs: () => request("/me/proximity-prefs"),
  setProximityPrefs: (body) => request("/me/proximity-prefs", { method: "PUT", body }),
  setGroupProximity: (id, sharing) => request(`/groups/${id}/proximity-prefs`, { method: "PUT", body: { sharing } }),
  getEventProximity: (id) => request(`/events/${id}/proximity`),
  setEventProximity: (id, is_sharing) => request(`/events/${id}/proximity`, { method: "PUT", body: { is_sharing } }),
  postEventLocation: (id, lat, lng) => request(`/events/${id}/location`, { method: "POST", body: { lat, lng } }),
  getEventLocations: (id) => request(`/events/${id}/location`),
  stopEventLocation: (id) => request(`/events/${id}/location`, { method: "DELETE" }),

  // blocking
  blockUser: (id) => request(`/users/${id}/block`, { method: "POST" }),
  unblockUser: (id) => request(`/users/${id}/block`, { method: "DELETE" }),
  myBlocks: () => request("/me/blocks"),

  // calendar sync preferences (per-group)
  getCalendarPrefs: (id) => request(`/groups/${id}/calendar-prefs`),
  setCalendarPrefs: (id, body) => request(`/groups/${id}/calendar-prefs`, { method: "PUT", body }),

  // feedback forum (app-level)
  listFeedback: (type) => request(`/feedback${type && type !== "all" ? `?type=${type}` : ""}`),
  createFeedback: (body) => request(`/feedback`, { method: "POST", body }),
  voteFeedback: (id) => request(`/feedback/${id}/vote`, { method: "POST" }),
  setFeedbackStatus: (id, status) => request(`/feedback/${id}/status`, { method: "PATCH", body: { status } }),
  editFeedback: (id, body) => request(`/feedback/${id}`, { method: "PATCH", body }),
  deleteFeedback: (id) => request(`/feedback/${id}`, { method: "DELETE" }),

  // threaded comments (parentType: 'event' | 'feedback') + likes
  listComments: (parentType, parentId) => request(`/comments/${parentType}/${parentId}`),
  addThreadComment: (parentType, parentId, text, threadParentId) =>
    request(`/comments/${parentType}/${parentId}`, { method: "POST", body: { text, thread_parent_id: threadParentId || null } }),
  likeComment: (commentId) => request(`/comment-like/${commentId}`, { method: "POST" }),

  // in-app notifications
  getNotifications: () => request(`/notifications`),
  readAllNotifications: () => request(`/notifications/read-all`, { method: "POST" }),
  readNotification: (id) => request(`/notifications/${id}/read`, { method: "POST" }),

  // web push (device/browser notifications)
  pushVapidKey: () => request(`/push/vapid-public-key`, { auth: false }),
  pushSubscribe: (body) => request(`/push/subscribe`, { method: "POST", body }),
  pushUnsubscribe: (body) => request(`/push/unsubscribe`, { method: "POST", body }),

  // friends & public profile
  userProfile: (id) => request(`/users/${id}/profile`),
  myEvents: () => request(`/me/events`),
  myCalendarEvents: (timeMin, timeMax) => request(`/me/calendar-events?time_min=${encodeURIComponent(timeMin)}&time_max=${encodeURIComponent(timeMax)}`),
  listFriends: () => request(`/friends`),
  friendRequests: () => request(`/friends/requests`),
  friendsFeed: () => request(`/friends/feed`),
  sendFriendRequest: (id) => request(`/friends/${id}/request`, { method: "POST" }),
  acceptFriend: (id) => request(`/friends/${id}/accept`, { method: "POST" }),
  declineFriend: (id) => request(`/friends/${id}/decline`, { method: "POST" }),
  removeFriend: (id) => request(`/friends/${id}`, { method: "DELETE" }),

  // 1:1 messaging (accepted friends)
  messageThreads: () => request(`/messages/threads`),
  conversation: (id) => request(`/messages/${id}`),
  sendMessage: (id, text) => request(`/messages/${id}`, { method: "POST", body: { text } }),
  messagesUnread: () => request(`/messages/unread-count`),

  // app admin
  getAbout: () => request(`/app/about`, { auth: false }),
  updateAbout: (body) => request(`/app/about`, { method: "PUT", body }),
  adminListUsers: (q) => request(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  adminUpdateUser: (id, body) => request(`/admin/users/${id}`, { method: "PATCH", body }),

  // automated plan import — personal upload (Profile) + per-group feed selector
  uploadImportFeed: (rows) => request(`/me/import-feed/upload`, { method: "POST", body: { rows } }),
  getGroupImportFeed: (gid) => request(`/groups/${gid}/import-feed`),
  setGroupImportFeed: (gid, source_group) => request(`/groups/${gid}/import-feed`, { method: "PUT", body: { source_group } }),

  // personal API token for the automated chat-import script (POST /api/chat-import)
  getApiToken: () => request(`/me/api-token`),
  generateApiToken: () => request(`/me/api-token`, { method: "POST" }),
  revokeApiToken: () => request(`/me/api-token`, { method: "DELETE" }),
  dedupeChatImport: () => request(`/admin/chat-import/dedupe`, { method: "POST" }),

  // Discovery + calendar overlays
  getDiscovery: (page = 0, size = 20, category) =>
    request(`/discovery?page=${page}&size=${size}${category && category !== "all" ? `&category=${encodeURIComponent(category)}` : ""}`),
  refreshDiscovery: () => request(`/discovery/refresh`, { method: "POST" }),
  addToCalendar: (payload) => request(`/discovery/add-to-calendar`, { method: "POST", body: payload }),
  getHolidays: (year) => request(`/overlays/holidays?year=${year}`),
  getMajorEvents: (from, to) => request(`/overlays/major-events?from=${from}&to=${to}`),

  // Partiful personal calendar
  getPartiful: () => request(`/me/partiful`),
  connectPartiful: (url) => request(`/me/partiful`, { method: "POST", body: { url } }),
  disconnectPartiful: () => request(`/me/partiful`, { method: "DELETE" }),
  getGroupPartiful: (gid) => request(`/groups/${gid}/partiful`),

  // custom HTML sections (app-admin managed; render on home + about pages)
  // HTML is base64-encoded so edge WAFs don't flag raw markup/scripts in the body.
  listSections: (page) => request(`/sections/${page}`, { auth: false }),
  myActivity: () => request("/me/activity"),
  markActivitySeen: () => request("/me/activity/seen", { method: "POST" }),
  createSection: (page, html) => request(`/sections/${page}`, { method: "POST", body: { html_b64: b64(html) } }),
  updateSection: (id, body) => {
    const b = { ...body };
    if (b.html !== undefined) { b.html_b64 = b64(b.html); delete b.html; }
    return request(`/sections/${id}`, { method: "PATCH", body: b });
  },
  deleteSection: (id) => request(`/sections/${id}`, { method: "DELETE" }),
  reorderSections: (page, ids) => request(`/sections/${page}/reorder`, { method: "PUT", body: { ids } }),

  // availability — manual "I'm free" + member status list (per group)
  memberStatus: (gid) => request(`/groups/${gid}/member-status`),
  setFree: (gid, hours) => request(`/groups/${gid}/status/free`, { method: "POST", body: { hours } }),
  clearFree: (gid) => request(`/groups/${gid}/status/free`, { method: "DELETE" }),

  // personal calendar sharing — visibility tiers (Phase 3)
  listPersonalCalendars: () => request(`/personal-calendars`),
  setPersonalCalendar: (calId, body) => request(`/personal-calendars/${encodeURIComponent(calId)}`, { method: "PUT", body }),
  googleAccounts: () => request(`/me/google-accounts`),
  connectGoogleAccount: (returnUrl) => request(`/me/google-accounts/connect?return=${encodeURIComponent(returnUrl)}`),
  disconnectGoogleAccount: (id) => request(`/me/google-accounts/${encodeURIComponent(id)}`, { method: "DELETE" }),
  getCalendarVisibility: (gid) => request(`/groups/${gid}/calendar-visibility`),
  setCalendarVisibility: (gid, enabled) => request(`/groups/${gid}/calendar-visibility`, { method: "PUT", body: { enabled } }),
  groupSchedule: (gid, timeMin, timeMax) => request(`/groups/${gid}/schedule?time_min=${encodeURIComponent(timeMin)}&time_max=${encodeURIComponent(timeMax)}`),
};

// Multipart image upload — no JSON Content-Type.
export async function uploadImage(file) {
  const form = new FormData();
  form.append("file", file, file.name || `upload-${Date.now()}.jpg`);
  const token = getToken();
  const res = await fetch(`${BACKEND_URL}/api/media`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!res.ok) throw new Error("Image upload failed");
  const data = await res.json();
  return data.url; // e.g. /api/media/{id}
}
